'use client';

import { useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, LogIn } from 'lucide-react';

interface StartSheetProps {
  open: boolean;
  onClose: () => void;
}

export default function StartSheet({ open, onClose }: StartSheetProps) {
  const router = useRouter();

  // Close on ESC
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, handleKeyDown]);

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Mobile: bottom sheet / Desktop: center modal */}
          <motion.div
            initial={{ y: '100%', opacity: 0.5 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 30, stiffness: 350 }}
            className="fixed z-50 inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center"
          >
            <div
              className="bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md mx-auto p-6 pb-8 sm:p-8 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Handle bar (mobile) */}
              <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-6 sm:hidden" />

              <h2 className="text-xl font-extrabold text-gray-900 text-center mb-2">
                어떻게 오셨나요?
              </h2>
              <p className="text-sm text-gray-400 text-center mb-6">
                상황에 맞는 경로로 안내해드릴게요.
              </p>

              <div className="space-y-3">
                {/* Option 1: New user */}
                <button
                  onClick={() => {
                    onClose();
                    router.push('/onboarding');
                  }}
                  className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-gray-100 bg-white hover:border-indigo-200 hover:bg-indigo-50/50 transition-all active:scale-[0.98] group text-left"
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110"
                    style={{ background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)' }}
                  >
                    <ArrowRight className="w-5 h-5 text-indigo-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-900">처음 왔어요</p>
                    <p className="text-xs text-gray-400 mt-0.5">나에게 맞는 코치를 찾아볼게요</p>
                  </div>
                </button>

                {/* Option 2: Returning user */}
                <button
                  onClick={() => {
                    onClose();
                    router.push('/auth/login');
                  }}
                  className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-gray-100 bg-white hover:border-indigo-200 hover:bg-indigo-50/50 transition-all active:scale-[0.98] group text-left"
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110"
                    style={{ background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)' }}
                  >
                    <LogIn className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-900">이전에 써본 적 있어요</p>
                    <p className="text-xs text-gray-400 mt-0.5">로그인하고 이어서 시작해요</p>
                  </div>
                </button>
              </div>

              {/* Dismiss */}
              <button
                onClick={onClose}
                className="w-full mt-4 py-2 text-sm text-gray-400 hover:text-gray-600 transition-colors"
              >
                닫기
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
